import {
	DataGrid,
	GridColDef,
	GridPagination,
	GridPaginationModel,
	GridRenderCellParams,
	GridValueFormatterParams,
	GridValueGetterParams
} from "@mui/x-data-grid";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import React, { ChangeEvent, FC, FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, InputAdornment, Menu, MenuItem, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";

import {
	ANDROID_USER_USERNAME,
	ROWS_PER_PAGE,
	USER_ROLE_ADMIN_ID,
	USER_ROLE_STAFF_ID,
	USER_ROLE_TYPES_E
} from "../../../utils/constants";
import {
	useDeleteUserMutation,
	useGetUserActivityLogsQuery,
	useGetUsersListQuery
} from "../../../redux/reducers/configuration.reducer";
import { useAppDispatch, useAppSelector } from "../../../redux";
import UserDetailsDialog from "./UserDetailsDialog";
import TablePagination from "../../../common/TablePagination";
import ConfirmationDialog from "../../../common/ConfirmationDialog";
import UserActivityDetailsDialog from "./UserActivityDetailsDialog";
import { useGetSiteConfigQuery } from "../../../redux/reducers/settings.reducer";
import { RESTRICTED_ACCESS_E, getRestrictedAccessList } from "../../../utils/commonUtils";
import { IGetUserActivityLogsRequestData, IGetUsersListRequestData, IUserListDetails } from "../../../interfaces";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";

export interface IUserDetails {
	id: number;
	sr_no: number;
	username: string;
	first_name: string;
	last_name: string;
	contact_number: string;
	last_login: string;
	user_type: number;
	address: string;
}

export interface IUserActivityDetails {
	id: number;
	sr_no: number;
	timestamp: string;
	user_name: string;
	activity: string;
}

enum USER_MANAGEMENT_TAB_E {
	USERS,
	ACTIVITY_LOG
}

type UserManagementTabRendererType = {
	[key in USER_MANAGEMENT_TAB_E]: ReactNode;
};

const UsersListPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ spacer: "grid-pagination-spacer-custom" }}
	/>
);

const UserManagement: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const searchInputRef = useRef<HTMLInputElement>(null);
	const userTypeDropdownAnchorRef = useRef<HTMLDivElement>(null);

	const [searchInput, setSearchInput] = useState<string>("");
	const [filters, setFilters] = useState<IGetUsersListRequestData>({ page: 1 });
	const [showUserDetailsDialog, setShowUserDetailsDialog] = useState<boolean>(false);
	const [selectedUserDetails, setSelectedUserDetails] = useState<IUserDetails | null>(null);

	const [deleteUserId, setDeleteUserId] = useState<number>(-1);
	const [selectedTab, setSelectedTab] = useState<USER_MANAGEMENT_TAB_E>(USER_MANAGEMENT_TAB_E.USERS);
	const [selectedActivityLog, setSelectedActivityLog] = useState<IUserActivityDetails | null>(null);

	const [userTypeDropdownOpen, setUserTypeDropdownOpen] = useState<boolean>(false);
	const [activityLogsFilters, setActivityLogsFilters] = useState<IGetUserActivityLogsRequestData>({ page: 1 });

	// APIS
	// GET USERS LIST
	const {
		data: getUsersListResponse,
		isLoading: getUsersListLoading,
		isFetching: getUsersListFetching,
		refetch: refetchUsersList
	} = useGetUsersListQuery(filters);

	// DELETE USER
	const [
		deleteUser,
		{ isLoading: deleteUserLoading, isSuccess: deleteUserSuccess, error: deleteUserError, reset: resetDeleteUserState }
	] = useDeleteUserMutation();

	// GET SITE CONFIG
	const { data: siteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	// GET USER ACTIVITY LOGS
	const {
		data: getUserActivityLogsResponse,
		isLoading: getUserActivityLogsLoading,
		isFetching: getUserActivityLogsFetching
	} = useGetUserActivityLogsQuery(activityLogsFilters);

	const usersList = getUsersListResponse?.results ?? [];

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(siteConfig?.cloud_sync_enabled ?? true),
		[siteConfig]
	);

	const activityLogsRowsData = useMemo<IUserActivityDetails[]>(() => {
		if (getUserActivityLogsResponse) {
			const activePage = filters.page ? filters.page - 1 : 0;

			return getUserActivityLogsResponse.results.map((item, index) => ({
				id: item.id,
				sr_no: activePage * ROWS_PER_PAGE + index + 1,
				timestamp: item.timestamp,
				user_name: item.name,
				activity: item.activity
			}));
		}

		return [];
	}, [filters.page, getUserActivityLogsResponse]);

	function handleOpenUserDetailsDialog(userDetails: IUserDetails | null) {
		setSelectedUserDetails(userDetails);
		setShowUserDetailsDialog(true);
	}

	function handleCloseUserDetailsDialog() {
		setShowUserDetailsDialog(false);
		setSelectedUserDetails(null);
		refetchUsersList();
	}

	function handleOpenDeleteConfirmationDialog(userId: number) {
		setDeleteUserId(userId);
	}

	function handleCloseDeleteConfirmationDialog() {
		setDeleteUserId(-1);
	}

	function handleDeleteUser() {
		if (deleteUserId >= 0) deleteUser(deleteUserId);
	}

	function handleChangeSearchInput(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		setSearchInput(event.target.value);
	}

	function handleClearSearchInput() {
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };
			delete updatedFilters.search;
			return updatedFilters;
		});

		setSearchInput("");

		if (searchInputRef.current) searchInputRef.current.focus();
	}

	function handleSearchUser(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (searchInput) updatedFilters.search = searchInput;
			else delete updatedFilters.search;

			updatedFilters.page = 1;

			return updatedFilters;
		});
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFiltersState) => ({
			...currentFiltersState,
			page: event.page + 1
		}));
	}

	function getUserTypeName(ownerTypeId: number) {
		return USER_ROLE_TYPES_E[ownerTypeId] ?? "";
	}

	function handleOpenUserTypeDropdown() {
		setUserTypeDropdownOpen(true);
	}

	function handleCloseUserTypeDropdown() {
		setUserTypeDropdownOpen(false);
	}

	function handleChangeSelectedUserType(updatedValue: number) {
		handleCloseUserTypeDropdown();
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (updatedValue >= 0) updatedFilters.role = updatedValue;
			else delete updatedFilters.role;

			updatedFilters.page = 1;

			return updatedFilters;
		});
	}

	function getUsersListRowsData(usersList: IUserListDetails[]): IUserDetails[] {
		const activePage = filters.page ? filters.page - 1 : 0;

		return usersList.map((userItem, index) => ({
			id: userItem.id,
			sr_no: activePage * ROWS_PER_PAGE + index + 1,
			username: userItem.username,
			first_name: userItem.first_name,
			last_name: userItem.last_name,
			contact_number: userItem.phone_number,
			last_login: userItem.last_login ?? "",
			user_type: userItem.role,
			address: userItem.address
		}));
	}

	function handleChangeSelectedTab(value: USER_MANAGEMENT_TAB_E) {
		setSelectedTab(value);
	}

	function handleOpenUserActivityDetailsDialog(activityDetails: IUserActivityDetails) {
		setSelectedActivityLog(activityDetails);
	}

	function handleCloseUserActivityDetailsDialog() {
		setSelectedActivityLog(null);
	}

	function handleChangeActivityLogsPage(event: GridPaginationModel) {
		setActivityLogsFilters((currentFilters) => ({
			...currentFilters,
			page: event.page + 1
		}));
	}

	useEffect(() => {
		if (
			RestrictedAccess.includes(RESTRICTED_ACCESS_E.USERS_PAGE) &&
			authState.user.token &&
			authState.user.token !== "invalid"
		) {
			navigate("/configuration");
		}
	}, [RestrictedAccess, authState.user.token, navigate]);

	useEffect(() => {
		if (deleteUserSuccess) {
			resetDeleteUserState();
			handleCloseDeleteConfirmationDialog();
			refetchUsersList();
		}
	}, [deleteUserSuccess, refetchUsersList, resetDeleteUserState]);

	useEffect(() => {
		if (deleteUserError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in deleteUserError && deleteUserError.data ? String(deleteUserError.data) : "Failed to delete user",
					severity: "error"
				})
			);

			resetDeleteUserState();
		}
	}, [deleteUserError, dispatch, resetDeleteUserState]);

	function renderUserTypeDropdown() {
		return (
			<Box>
				<Box
					component="div"
					className="table-column-header-dropdown-wrapper align-center"
					id="user-type-dropdown"
					aria-controls="user-type-dropdown-menu"
					aria-haspopup
					aria-expanded={userTypeDropdownOpen}
					onClick={handleOpenUserTypeDropdown}
					ref={userTypeDropdownAnchorRef}
				>
					<Typography variant="subtitle2" textTransform="capitalize">
						{filters.role ? getUserTypeName(filters.role) : "Owner Type"}
					</Typography>
					{userTypeDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="user-type-dropdown-menu"
					anchorEl={userTypeDropdownAnchorRef.current}
					open={userTypeDropdownOpen}
					onClose={handleCloseUserTypeDropdown}
					MenuListProps={{ "aria-labelledby": "user-type-dropdown" }}
					sx={{ width: "100%" }}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					transformOrigin={{ vertical: "top", horizontal: "center" }}
				>
					<MenuItem onClick={() => handleChangeSelectedUserType(-1)} sx={{ minWidth: 130 }}>
						<em>All</em>
					</MenuItem>

					{Object.entries(USER_ROLE_TYPES_E).map(([id, name]) => (
						<MenuItem key={id} onClick={() => handleChangeSelectedUserType(Number(id))}>
							{name}
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 80,
			minWidth: 60,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "full_name",
			headerName: "Name",
			flex: 1,
			minWidth: 120,
			sortable: false,
			headerAlign: "center",
			align: "center",
			valueGetter: (params: GridValueGetterParams<IUserDetails>) => `${params.row.first_name} ${params.row.last_name}`
		},
		{
			field: "username",
			headerName: "Username",
			flex: 1,
			minWidth: 120,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "contact_number",
			headerName: "Contact Number",
			flex: 1,
			minWidth: 120,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "last_login",
			headerName: "Last Login",
			flex: 1,
			minWidth: 120,
			sortable: false,
			headerAlign: "center",
			align: "center",
			valueFormatter: (params: GridValueFormatterParams<string>) =>
				params.value ? moment(params.value).format("DD/MM/YYYY hh:mm A") : "--"
		},
		{
			field: "user_type",
			headerName: "User Type",
			flex: 1,
			minWidth: 120,
			sortable: false,
			align: "center",
			renderHeader: renderUserTypeDropdown,
			valueGetter: (params: GridValueGetterParams<IUserDetails>) => USER_ROLE_TYPES_E[params.value ?? 0]
			// renderCell: (params: GridRenderCellParams<IUserDetails>) => (
			// 	<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
			// 		<Typography variant="body2" sx={{ flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
			// 			{USER_ROLE_TYPES_E[params.value ?? 0]}
			// 		</Typography>

			// 		<Box>
			// 			{params.row.username !== ANDROID_USER_USERNAME &&
			// 			!(
			// 				RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_ADMIN) &&
			// 				[USER_ROLE_ADMIN_ID, USER_ROLE_STAFF_ID].includes(params.row.user_type)
			// 			) ? (
			// 				<IconButton size="small" onClick={() => handleOpenDeleteConfirmationDialog(params.row.id)}>
			// 					<DeleteOutlineIcon fontSize="inherit" color="error" />
			// 				</IconButton>
			// 			) : null}

			// 			<IconButton size="small" onClick={() => handleOpenUserDetailsDialog(params.row)}>
			// 				<InfoOutlinedIcon fontSize="inherit" sx={{ color: "#1e53af" }} />
			// 			</IconButton>
			// 		</Box>
			// 	</Box>
			// )
		},
		{
			field: "actions",
			headerName: "",
			width: !RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_ADMIN) ? 70 : 50,
			minWidth: !RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_ADMIN) ? 70 : 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IUserDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
					{params.row.username !== ANDROID_USER_USERNAME &&
					!(
						RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_ADMIN) &&
						[USER_ROLE_ADMIN_ID, USER_ROLE_STAFF_ID].includes(params.row.user_type)
					) ? (
						<IconButton size="small" onClick={() => handleOpenDeleteConfirmationDialog(params.row.id)}>
							<DeleteOutlineIcon fontSize="inherit" color="error" />
						</IconButton>
					) : null}

					<IconButton size="small" onClick={() => handleOpenUserDetailsDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" sx={{ color: "#1e53af" }} />
					</IconButton>
				</Box>
			)
		}
	];

	const activityColumnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 80,
			minWidth: 60,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "timestamp",
			headerName: "Timestamp",
			flex: 1,
			minWidth: 120,
			sortable: false,
			headerAlign: "center",
			align: "center",
			valueFormatter: (params: GridValueFormatterParams<string>) => moment(params.value).format("DD/MM/YYYY hh:mm A")
		},
		{
			field: "user_name",
			headerName: "User Name",
			flex: 1,
			minWidth: 120,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "activity",
			headerName: "Activity",
			flex: 2,
			minWidth: 150,
			sortable: false
		},
		{
			field: "actions",
			headerName: "",
			width: 50,
			minWidth: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IUserActivityDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton size="small" onClick={() => handleOpenUserActivityDetailsDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" sx={{ color: "#1e53af" }} />
					</IconButton>
				</Box>
			)
		}
	];

	const TabRenderer: UserManagementTabRendererType = {
		[USER_MANAGEMENT_TAB_E.USERS]: (
			<>
				<Box className="user-management-table-header-wrapper">
					<Box className="user-management-table-actions-wrapper">
						<Button
							variant="outlined"
							color="success"
							startIcon={<AddCircleIcon />}
							sx={{ paddingX: 3 }}
							onClick={() => handleOpenUserDetailsDialog(null)}
						>
							Add
						</Button>
					</Box>

					<Box
						className="user-management-table-actions-wrapper"
						component="form"
						noValidate
						onSubmit={handleSearchUser}
					>
						<TextField
							variant="filled"
							placeholder="Search"
							size="small"
							value={searchInput}
							onChange={handleChangeSearchInput}
							inputRef={searchInputRef}
							sx={{ width: "100%", maxWidth: "300px" }}
							autoComplete="off"
							InputProps={{
								disableUnderline: true,
								hiddenLabel: true,
								classes: { root: "filled-input-root dashboard-search-input" },
								startAdornment: (
									<InputAdornment position="start">
										<IconButton edge="start" size="small" type="submit" color="primary">
											<SearchIcon fontSize="medium" />
										</IconButton>
									</InputAdornment>
								),
								endAdornment: filters.search ? (
									<InputAdornment position="end">
										<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
											<CloseIcon fontSize="inherit" />
										</IconButton>
									</InputAdornment>
								) : null
							}}
						/>
					</Box>
				</Box>

				<Box className="user-management-table-wrapper">
					<DataGrid
						columns={columnsData}
						rows={getUsersListRowsData(usersList ?? [])}
						getRowId={(item) => item.id}
						loading={getUsersListLoading || getUsersListFetching}
						disableColumnMenu
						disableRowSelectionOnClick
						density="compact"
						rowSpacingType="border"
						columnHeaderHeight={65}
						paginationMode="server"
						pageSizeOptions={[ROWS_PER_PAGE]}
						onPaginationModelChange={handlePageChange}
						rowCount={getUsersListResponse?.count ?? 0}
						paginationModel={{ page: filters.page ? filters.page - 1 : 0, pageSize: ROWS_PER_PAGE }}
						slots={{ pagination: UsersListPagination }}
						slotProps={{ footer: { sx: { justifyContent: "center" } } }}
						hideFooter={(getUsersListResponse?.count ?? 0) <= ROWS_PER_PAGE}
						sx={{
							"& .MuiDataGrid-columnHeader": {
								fontSize: { xs: "12px", md: "14px" }
							},
							"& .MuiDataGrid-cell": {
								fontSize: { xs: "12px", md: "14px" }
							}
						}}
						classes={{
							columnSeparator: "user-management-table-column-separator",
							columnHeader: "user-management-table-column-headers",
							cell: "user-management-table-cell",
							root: "user-management-table-root"
						}}
					/>
				</Box>
			</>
		),
		[USER_MANAGEMENT_TAB_E.ACTIVITY_LOG]: (
			<Box className="user-management-table-wrapper">
				<DataGrid
					columns={activityColumnsData}
					rows={activityLogsRowsData}
					loading={getUserActivityLogsLoading || getUserActivityLogsFetching}
					disableColumnMenu
					disableRowSelectionOnClick
					density="compact"
					rowSpacingType="border"
					columnHeaderHeight={65}
					paginationMode="server"
					pageSizeOptions={[ROWS_PER_PAGE]}
					onPaginationModelChange={handleChangeActivityLogsPage}
					rowCount={getUserActivityLogsResponse?.count ?? 0}
					paginationModel={{ page: activityLogsFilters.page - 1, pageSize: ROWS_PER_PAGE }}
					slots={{ pagination: UsersListPagination }}
					slotProps={{ footer: { sx: { justifyContent: "center" } } }}
					hideFooter={(getUserActivityLogsResponse?.count ?? 0) <= ROWS_PER_PAGE}
					sx={{
						"& .MuiDataGrid-columnHeader": {
							fontSize: { xs: "12px", md: "14px" }
						},
						"& .MuiDataGrid-cell": {
							fontSize: { xs: "12px", md: "14px" }
						}
					}}
					classes={{
						columnSeparator: "user-management-table-column-separator",
						columnHeader: "user-management-table-column-headers",
						cell: "user-management-table-cell"
					}}
				/>
			</Box>
		)
	};

	return (
		<Box className="configuration-screen-wrapper flex-wrapper card-layout">
			<Box className="user-management-tab-selection-wrapper">
				<Button
					disableElevation
					variant={selectedTab === USER_MANAGEMENT_TAB_E.USERS ? "contained" : "outlined"}
					color="primary"
					onClick={() => handleChangeSelectedTab(USER_MANAGEMENT_TAB_E.USERS)}
					startIcon={<SupervisedUserCircleIcon />}
					sx={{ width: { xs: "100%", md: "unset" } }}
				>
					Users
				</Button>

				<Button
					disableElevation
					variant={selectedTab === USER_MANAGEMENT_TAB_E.ACTIVITY_LOG ? "contained" : "outlined"}
					color="primary"
					onClick={() => handleChangeSelectedTab(USER_MANAGEMENT_TAB_E.ACTIVITY_LOG)}
					startIcon={<ListAltIcon />}
					sx={{ width: { xs: "100%", md: "unset" } }}
				>
					Activity Log
				</Button>
			</Box>

			{TabRenderer[selectedTab]}

			<UserDetailsDialog
				open={showUserDetailsDialog}
				userDetails={selectedUserDetails}
				onClose={handleCloseUserDetailsDialog}
			/>

			<ConfirmationDialog
				open={deleteUserId >= 0}
				title="Delete User"
				heading="Are you sure you want to delete this user?"
				onClose={handleCloseDeleteConfirmationDialog}
				onConfirm={handleDeleteUser}
				color="error"
				width="sm"
				confirmButtonLoading={deleteUserLoading}
			/>

			<UserActivityDetailsDialog
				open={!!selectedActivityLog}
				onClose={handleCloseUserActivityDetailsDialog}
				activityDetails={selectedActivityLog}
			/>
		</Box>
	);
};

export default UserManagement;
